import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { assignmentAPI } from '@/utils/api';
import api from '@/utils/api';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { ClipboardList, Video, Search, X, FolderOpen, Database } from 'lucide-react';
import { toast } from 'sonner';

const TranslatorAssignments = () => {
  const { user } = useOutletContext();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('projects');

  // My Projects state
  const [myProjects, setMyProjects] = useState([]);
  const [projectTitles, setProjectTitles] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectSearch, setProjectSearch] = useState('');

  // General search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  useEffect(() => {
    loadAssignments();
    loadMyProjects();
  }, []);

  // Debounced general search
  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    if (searchQuery.trim().length < 2) { setSearchResults(null); return; }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/project-search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(res.data);
      } catch (e) {
        console.error('Search error:', e);
      } finally {
        setSearching(false);
      }
    }, 400);
    setSearchTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const loadAssignments = async () => {
    try {
      const response = await assignmentAPI.list({});
      setAssignments(response.data);
    } catch (error) {
      console.error('Error loading assignments:', error);
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const loadMyProjects = async () => {
    try {
      const res = await api.get('/translator/my-projects');
      setMyProjects(res.data.projects || []);
      setProjectTitles(res.data.project_titles || []);
    } catch (error) {
      console.error('Error loading my projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };

  const filteredProjects = useMemo(() => {
    if (!projectSearch.trim()) return myProjects;
    const q = projectSearch.toLowerCase();
    return myProjects.filter(p =>
      Object.values(p).some(v => String(v).toLowerCase().includes(q))
    );
  }, [myProjects, projectSearch]);

  const getStatusColor = (status) => {
    const colors = {
      assigned: 'bg-accent/10 text-accent',
      in_progress: 'bg-creative/10 text-creative',
      submitted: 'bg-warning/10 text-warning',
      approved: 'bg-success/10 text-success',
      invoiced: 'bg-primary/10 text-primary',
      paid: 'bg-success/10 text-success',
    };
    return colors[status] || 'bg-secondary text-secondary-foreground';
  };

  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-heading font-bold text-primary mb-2">My Assignments</h1>
          <p className="text-muted-foreground">Your projects and assignments</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="projects" className="flex items-center gap-1">
              <FolderOpen className="w-4 h-4" /> My Projects ({myProjects.length})
            </TabsTrigger>
            <TabsTrigger value="assignments" className="flex items-center gap-1">
              <ClipboardList className="w-4 h-4" /> Assignments ({assignments.length})
            </TabsTrigger>
            <TabsTrigger value="search" className="flex items-center gap-1">
              <Search className="w-4 h-4" /> Search All
            </TabsTrigger>
          </TabsList>

          {/* My Projects Tab */}
          <TabsContent value="projects" className="space-y-4">
            {projectTitles.length > 0 && (
              <div className="flex gap-1 flex-wrap">
                <span className="text-sm text-muted-foreground mr-1">Mapped to:</span>
                {projectTitles.map(t => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}
              </div>
            )}

            {myProjects.length > 0 && (
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  placeholder="Filter your projects..."
                  className="pl-10"
                  data-testid="my-projects-filter"
                />
              </div>
            )}

            {projectsLoading ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Loading projects...</p>
            ) : myProjects.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Database className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                  <h3 className="font-semibold mb-1">No Projects Assigned</h3>
                  <p className="text-sm text-muted-foreground">
                    {projectTitles.length > 0
                      ? `You're mapped to ${projectTitles.join(', ')} but no matching projects exist yet.`
                      : 'Ask your admin to map you to a GTS Pro email to see your projects.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10 text-center">#</TableHead>
                          <TableHead className="text-center whitespace-nowrap">Main Title</TableHead>
                          <TableHead className="text-center whitespace-nowrap">Episode Title</TableHead>
                          <TableHead className="text-center whitespace-nowrap">Task</TableHead>
                          <TableHead className="text-center whitespace-nowrap">Language</TableHead>
                          <TableHead className="text-center whitespace-nowrap">Duration (QTY)</TableHead>
                          <TableHead className="text-center whitespace-nowrap">Due Date</TableHead>
                          <TableHead className="text-center whitespace-nowrap">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredProjects.map((p, i) => (
                          <TableRow key={p.project_id || i}>
                            <TableCell className="text-center text-muted-foreground text-xs">{i + 1}</TableCell>
                            <TableCell className="text-center text-sm">{p.main_title || '—'}</TableCell>
                            <TableCell className="text-center text-sm">{p.episode_title || '—'}</TableCell>
                            <TableCell className="text-center text-sm">{p.task || '—'}</TableCell>
                            <TableCell className="text-center text-sm">{p.language || '—'}</TableCell>
                            <TableCell className="text-center text-sm font-medium">{p.qty > 0 ? p.qty : '—'}</TableCell>
                            <TableCell className="text-center text-sm">{p.due_date || '—'}</TableCell>
                            <TableCell className="text-center">
                              {p.task_status === 'completed'
                                ? <Badge className="bg-green-100 text-green-700 text-[10px]">Completed</Badge>
                                : <Badge className="bg-blue-100 text-blue-700 text-[10px]">In Progress</Badge>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Assignments Tab */}
          <TabsContent value="assignments" className="space-y-4">
            {assignments.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <ClipboardList className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                  <h3 className="font-semibold mb-1">No assignments yet</h3>
                  <p className="text-sm text-muted-foreground">You'll see your video assignments here once they're assigned by the admin</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {assignments.map((assignment) => (
                  <Card key={assignment.assignment_id} className="hover:shadow-md transition-shadow" data-testid={`assignment-card-${assignment.assignment_id}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Video className="w-5 h-5 text-accent" />
                            <h3 className="text-lg font-semibold text-primary">{assignment.video_title}</h3>
                          </div>
                          <div className="space-y-1 text-sm text-muted-foreground mb-3">
                            <p><span className="font-medium">Client:</span> {assignment.client_name}</p>
                            <p><span className="font-medium">Project:</span> {assignment.project_name}</p>
                            <p><span className="font-medium">Language:</span> {assignment.language}</p>
                            <p><span className="font-medium">Task Type:</span> {assignment.task_type}</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge className={getStatusColor(assignment.status)}>
                              {assignment.status.replace('_', ' ')}
                            </Badge>
                            {assignment.due_date && (
                              <Badge variant="outline" className="text-xs">Due: {formatDate(assignment.due_date)}</Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right space-y-2">
                          <div>
                            <p className="text-sm text-muted-foreground">Duration</p>
                            <p className="text-xl font-bold text-primary">{assignment.video_duration} min</p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Rate</p>
                            <p className="text-lg font-semibold text-accent">{formatCurrency(assignment.rate_per_minute)}/min</p>
                          </div>
                          <div className="pt-2 border-t border-border">
                            <p className="text-sm text-muted-foreground">Total</p>
                            <p className="text-xl font-bold text-success">
                              {formatCurrency((assignment.minutes_worked || assignment.video_duration) * assignment.rate_per_minute)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Search All Tab */}
          <TabsContent value="search" className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by title, PO number, episode, client..."
                className="pl-10 pr-10"
                data-testid="translator-project-search"
              />
              {searchQuery && (
                <button onClick={() => { setSearchQuery(''); setSearchResults(null); }} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                </button>
              )}
            </div>
            {searching && <p className="text-sm text-muted-foreground">Searching...</p>}
            {searchResults && !searching && (
              <div className="space-y-2">
                {searchResults.projects.length === 0 && searchResults.billings.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No results for "{searchQuery}"</p>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">
                      {searchResults.projects.length} projects, {searchResults.billings.length} billing records
                    </p>
                    <div className="max-h-80 overflow-y-auto space-y-1.5">
                      {searchResults.projects.map((p, i) => (
                        <div key={`p-${i}`} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border bg-muted/30 text-sm">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{p.main_title || 'Untitled'}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {[p.episode_title, p.client, p.language, p.task].filter(Boolean).join(' · ')}
                            </p>
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="text-xs font-mono text-muted-foreground">{p.po_number}</p>
                            {p.qty > 0 && <p className="text-xs text-muted-foreground">{p.qty} min</p>}
                          </div>
                        </div>
                      ))}
                      {searchResults.billings.map((b, i) => (
                        <div key={`b-${i}`} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border text-sm">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{b.main_title || 'Untitled'}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {[b.episode_title, b.client, b.target_language, b.task].filter(Boolean).join(' · ')}
                            </p>
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="text-xs font-mono text-muted-foreground">{b.po_number}</p>
                            {b.qty > 0 && <p className="text-xs text-muted-foreground">{b.qty} min</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            {!searchQuery && !searchResults && (
              <p className="text-sm text-muted-foreground py-8 text-center">Type to search across all projects and billing records</p>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default TranslatorAssignments;
